import { useEffect, useMemo, useState } from 'react';
import { useLocalSearchParams } from 'expo-router';
import { Platform, Pressable, ScrollView, StyleSheet, Text, TextInput, View } from 'react-native';
import {
  generateCourseAssets,
  getCourse,
  ingestMaterial,
  listAssets,
  parsePdfMaterialViaJob,
  publishCourse,
  reviewAsset,
  uploadPdfMaterial,
  uploadTextMaterial,
} from '@/services/learnAiService';

function hashText(input: string) {
  let h = 0;
  for (let i = 0; i < input.length; i++) {
    h = (Math.imul(31, h) + input.charCodeAt(i)) | 0;
  }
  return `txt-${Math.abs(h)}`;
}

export default function LearnAiCoursePipelineScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const [rawText, setRawText] = useState('');
  const [message, setMessage] = useState('');
  const [assets, setAssets] = useState<any[]>([]);
  const [course, setCourse] = useState<any | null>(null);
  const [busy, setBusy] = useState(false);
  const [messageTone, setMessageTone] = useState<'success' | 'error' | 'info'>('info');
  const [selectedPdf, setSelectedPdf] = useState<(Blob & { name?: string; type?: string; size?: number }) | null>(null);

  const courseId = useMemo(() => String(id || ''), [id]);

  async function refreshAssets() {
    if (!courseId) return;
    const data = (await listAssets(courseId)) ?? [];
    setAssets(data);
  }

  useEffect(() => {
    if (!courseId) return;
    getCourse(courseId).then(setCourse).catch((error) => setMessage(error.message));
    refreshAssets().catch((error) => setMessage(error.message));
  }, [courseId]);

  async function handleUploadAndIngest() {
    if (!courseId) return;
    if (!rawText.trim()) {
      setMessage('יש להזין תוכן חומר לימוד (PDF/Text).');
      setMessageTone('error');
      return;
    }

    try {
      setBusy(true);
      const words = rawText.trim().split(/\s+/);
      const organizationId = course?.organization_id || assets[0]?.organization_id || undefined;
      if (!organizationId) {
        setMessage('יש להתחיל מה-Workspace ולוודא הרשאות ארגון.');
        setMessageTone('error');
        return;
      }

      const material: any = await uploadTextMaterial({
        organizationId,
        courseId,
        fileName: `manual-upload-${Date.now()}.txt`,
        rawText,
        fileHash: hashText(rawText),
      });
      const ingestResult = await ingestMaterial(material?.id);
      setMessage(`Ingestion done · chunks: ${ingestResult?.chunks_created ?? 0} · quality: ${ingestResult?.quality_score ?? '-'}`);
      setMessageTone('success');
      setRawText(words.length < 30 ? `${rawText}\n\n(המלצה: להעלות יותר תוכן לתוצרים חזקים יותר)` : '');
    } catch (error: any) {
      setMessage(error.message || 'Upload/Ingest נכשל');
      setMessageTone('error');
    } finally {
      setBusy(false);
    }
  }

  async function handlePickPdf() {
    if (Platform.OS !== 'web') {
      setMessage('בחירת PDF ישירה נתמכת כרגע ב-Web. במובייל אפשר להדביק טקסט זמנית.');
      setMessageTone('info');
      return;
    }

    const file = await new Promise<(Blob & { name?: string; type?: string; size?: number }) | null>((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'application/pdf,.pdf';
      input.onchange = () => {
        const picked = input.files?.[0] ?? null;
        resolve(picked as any);
      };
      input.click();
    });

    if (!file) return;
    setSelectedPdf(file);
    setMessage(`נבחר קובץ: ${file.name || 'material.pdf'}`);
    setMessageTone('info');
  }

  async function handleUploadPdfAndParse() {
    if (!courseId) return;
    if (!selectedPdf) {
      setMessage('יש לבחור קובץ PDF לפני העלאה.');
      setMessageTone('error');
      return;
    }

    try {
      setBusy(true);
      const organizationId = course?.organization_id || assets[0]?.organization_id || undefined;
      if (!organizationId) {
        setMessage('יש להתחיל מה-Workspace ולוודא הרשאות ארגון.');
        setMessageTone('error');
        return;
      }

      const material = await uploadPdfMaterial({
        organizationId,
        courseId,
        file: selectedPdf,
      });
      if (!material?.id) {
        throw new Error('material_create_failed');
      }

      if (material && 'dedupe_hit' in material && material.dedupe_hit) {
        setMessage(`הקובץ כבר קיים בקורס (v${material.version}). דילוג על העלאה כפולה.`);
        setMessageTone('info');
        setSelectedPdf(null);
        return;
      }

      const parseResult = await parsePdfMaterialViaJob(material.id);
      const chunks = parseResult?.ingest?.chunks_created ?? parseResult?.chunks_created ?? 0;
      const quality = parseResult?.ingest?.quality_score ?? parseResult?.quality_score ?? '-';
      setMessage(`PDF הועלה ונפרס · chunks: ${chunks} · quality: ${quality}`);
      setMessageTone('success');
      setSelectedPdf(null);
    } catch (error: any) {
      setMessage(error.message || 'PDF upload/parse נכשל');
      setMessageTone('error');
    } finally {
      setBusy(false);
    }
  }

  async function handleGenerate() {
    if (!courseId) return;
    try {
      setBusy(true);
      const result = await generateCourseAssets(courseId);
      setMessage(result?.partial ? 'Generation partial success: התוכן קצר, אבל נוצרו assets.' : 'Generation completed: כל התוצרים נוצרו ונשלחו ל-Review.');
      setMessageTone('success');
      await refreshAssets();
    } catch (error: any) {
      setMessage(error.message || 'Generation נכשל');
      setMessageTone('error');
    } finally {
      setBusy(false);
    }
  }

  async function handleReview(assetId: string, action: 'approve' | 'hide' | 'regenerate') {
    try {
      setBusy(true);
      await reviewAsset(assetId, action);
      await refreshAssets();
      setMessage(`Asset ${action} בוצע.`);
      setMessageTone('success');
    } catch (error: any) {
      setMessage(error.message || 'Review action failed');
      setMessageTone('error');
    } finally {
      setBusy(false);
    }
  }

  async function handlePublish() {
    try {
      setBusy(true);
      const result = await publishCourse(courseId);
      setMessage(`Course published. published_assets=${result?.published_assets ?? 0}`);
      setMessageTone('success');
      await refreshAssets();
    } catch (error: any) {
      setMessage(error.message || 'Publish failed');
      setMessageTone('error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <ScrollView contentContainerStyle={styles.container}>
      <Text style={styles.h1}>Course Pipeline</Text>
      <Text style={styles.sub}>Upload → Generate → Review → Publish</Text>

      <View style={styles.card}>
        <Text style={styles.cardTitle}>Upload Materials (Text/PDF extracted text)</Text>
        <View style={styles.row}>
          <Pressable style={[styles.smallBtn, styles.smallBtnSoft]} onPress={handlePickPdf} disabled={busy}>
            <Text style={styles.smallTxt}>בחר PDF</Text>
          </Pressable>
          <Pressable style={styles.smallBtn} onPress={handleUploadPdfAndParse} disabled={busy || !selectedPdf}>
            <Text style={styles.smallTxt}>Upload PDF + Parse Job</Text>
          </Pressable>
        </View>
        {selectedPdf ? <Text style={styles.muted}>נבחר: {selectedPdf.name} · {Math.round((selectedPdf.size || 0) / 1024)}KB</Text> : null}
        <TextInput
          style={[styles.input, styles.textarea]}
          multiline
          placeholder="Paste extracted text from PDF/summaries/slides"
          value={rawText}
          onChangeText={setRawText}
        />
        <Pressable style={styles.button} onPress={handleUploadAndIngest} disabled={busy}>
          <Text style={styles.buttonText}>Upload + Parse + Chunk</Text>
        </Pressable>
      </View>

      <View style={styles.card}>
        <Text style={styles.cardTitle}>Generate AI Layer</Text>
        <Pressable style={styles.button} onPress={handleGenerate} disabled={busy}>
          <Text style={styles.buttonText}>Generate Overview / Summaries / Practice / Exam / Chat Map</Text>
        </Pressable>
      </View>

      <View style={styles.card}>
        <Text style={styles.cardTitle}>AI Content Review</Text>
        {assets.length === 0 ? <Text style={styles.muted}>אין assets עדיין. יש להעלות חומר ולייצר תוצרים.</Text> : null}
        {assets.map((asset) => (
          <View key={asset.id} style={styles.assetCard}>
            <Text style={styles.assetTitle}>{asset.asset_type} · {asset.state}</Text>
            <View style={styles.row}>
              <Pressable style={styles.smallBtn} onPress={() => handleReview(asset.id, 'approve')}><Text style={styles.smallTxt}>Approve</Text></Pressable>
              <Pressable style={styles.smallBtn} onPress={() => handleReview(asset.id, 'hide')}><Text style={styles.smallTxt}>Hide</Text></Pressable>
              <Pressable style={styles.smallBtn} onPress={() => handleReview(asset.id, 'regenerate')}><Text style={styles.smallTxt}>Regenerate</Text></Pressable>
            </View>
          </View>
        ))}
      </View>

      <Pressable style={[styles.button, styles.publish]} onPress={handlePublish} disabled={busy}>
        <Text style={styles.buttonText}>Publish Course</Text>
      </Pressable>

      {!!message ? <Text style={[styles.message, messageTone === 'error' ? styles.messageError : messageTone === 'success' ? styles.messageSuccess : styles.messageInfo]}>{message}</Text> : null}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: { padding: 20, gap: 12, backgroundColor: '#F9FAFB' },
  h1: { fontSize: 28, fontWeight: '700', color: '#111827' },
  sub: { color: '#6B7280' },
  card: { backgroundColor: 'white', padding: 14, borderRadius: 12, gap: 8 },
  cardTitle: { fontWeight: '700', fontSize: 18, color: '#111827' },
  input: { borderWidth: 1, borderColor: '#D1D5DB', borderRadius: 8, padding: 10 },
  textarea: { minHeight: 140, textAlignVertical: 'top' },
  button: { backgroundColor: '#4C1D95', borderRadius: 10, alignItems: 'center', padding: 12 },
  buttonText: { color: 'white', fontWeight: '700' },
  muted: { color: '#6B7280' },
  assetCard: { borderWidth: 1, borderColor: '#E5E7EB', borderRadius: 10, padding: 10, gap: 8 },
  assetTitle: { color: '#111827', fontWeight: '600' },
  row: { flexDirection: 'row', gap: 8, flexWrap: 'wrap' },
  smallBtn: { backgroundColor: '#EDE9FE', borderRadius: 8, paddingHorizontal: 10, paddingVertical: 6 },
  smallBtnSoft: { backgroundColor: '#DDD6FE' },
  smallTxt: { color: '#4C1D95', fontWeight: '700' },
  publish: { marginTop: 2, backgroundColor: '#065F46' },
  message: { fontWeight: '700', marginBottom: 30 },
  messageSuccess: { color: '#065F46' },
  messageError: { color: '#991B1B' },
  messageInfo: { color: '#1D4ED8' },
});
